import { message } from 'antd';
import { useAuthorization } from 'components/Authorization';
import { useMemoizedFn } from 'ahooks';
import { noop } from 'lodash-es';
import { useCallback, useEffect, useMemo } from 'react';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { Role } from 'services/api/department/roleWithDepartment';
import { useNavigate } from 'react-router-dom';
import { useOrgValue } from './system/useOrg';
import { useOrgConfigStateValue } from './useOrgConfig';
import usePermission from './usePermission';
import { useTranslate } from '@tolgee/react';
import { useIncentiveType } from 'views/Management/NewuserIncentive/state';
import { queries } from 'services/queries';
import { useQuery } from '@tanstack/react-query';

export { permissionNameIdMap } from 'services/api/department/permissions';

export const newPermissionState = atom<{
  newPermissionEnable?: boolean;
  roles: Role[];
  loaded: boolean;
}>({
  newPermissionEnable: true,
  roles: [],
  loaded: false,
});

export const usePermissionLoaded = () => useAtomValue(newPermissionState).loaded;

export const useUpdateUserRoles = () => {
  const { t } = useTranslate();
  const nav = useNavigate();
  const setNewPermissionState = useSetAtom(newPermissionState);

  const {
    data: rolesData,
    refetch,
    error,
  } = useQuery({
    ...queries.accounts.roles,
    enabled: false,
    select: (data) => data.roles,
  });

  const formatRoles = (roles: Role[]) => {
    return roles.map<Role>(({ assignConversationConfig, permissionWithScopes, ...others }) => {
      return {
        ...others,
        assignConversationConfig,
        permissionWithScopes: permissionWithScopes.map((permission) => {
          const { permissionName } = permission;

          if (permissionName !== '快捷回复-个人模版') {
            return permission;
          }

          const superviserPermission = permissionWithScopes.find(
            (_) => _.permissionName === '管理员视角',
          );

          if (superviserPermission) {
            return {
              ...permission,
              platformStoreScopes: superviserPermission.platformStoreScopes,
            };
          }

          if (assignConversationConfig.assignConversation) {
            return {
              ...permission,
              platformStoreScopes: assignConversationConfig.platformStoreScopes,
            };
          }

          const groupPermission = permissionWithScopes.find(
            (_) => _.permissionName === '快捷回复-团队模版',
          );

          return {
            ...permission,
            platformStoreScopes: groupPermission?.platformStoreScopes || [],
          };
        }),
      };
    });
  };

  useEffect(() => {
    if (rolesData) {
      const roles = formatRoles(rolesData);
      setNewPermissionState({
        newPermissionEnable: true,
        roles,
        loaded: true,
      });
      // 当前帐户没有分配角色，直接重定向到提示页面
      if (roles.length === 0) {
        nav('/notRole', { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rolesData]);

  useEffect(() => {
    if (error) {
      setNewPermissionState((pre) => {
        if (pre.loaded) {
          return pre;
        }

        return {
          ...pre,
          loaded: true,
        };
      });

      message.error(t('department.获取用户权限失败'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  return useMemoizedFn(() => {
    return refetch();
  });
};

export const useSetupNewPermission = () => {
  const { payload } = useAuthorization<Oversea.Token.Payload>();

  const { orgId } = payload || {};

  const updateUserRoles = useUpdateUserRoles();

  useEffect(() => {
    if (!orgId) {
      return noop;
    }

    updateUserRoles();

    const timer = setInterval(() => {
      updateUserRoles();
    }, 1000 * 60 * 60 * 2);

    return () => {
      clearInterval(timer);
    };
  }, [orgId, updateUserRoles]);
};

const mergeRegionScopes = (
  current: General.StoreScope['regionStoreScopes'],
  next: General.StoreScope['regionStoreScopes'],
) => {
  return [...current, ...next].reduce<General.StoreScope['regionStoreScopes']>((res, cur) => {
    const { region, storeCount, storeIds } = cur;

    const regionTarget = res.find((_) => _.region === region);

    if (!regionTarget) {
      res.push({ ...cur });

      return res;
    }

    regionTarget.storeCount += storeCount;
    regionTarget.storeIds = [...(regionTarget.storeIds || []), ...(storeIds || [])];

    return res;
  }, []);
};

const mergePlatformScopes = (current: General.StoreScope[], next: General.StoreScope[]) => {
  return [...current, ...next].reduce<General.StoreScope[]>((res, cur) => {
    const { platform, regionStoreScopes, storeCount } = cur;

    const platformTarget = res.find((_) => _.platform === platform);

    if (!platformTarget) {
      res.push({ ...cur });

      return res;
    }

    platformTarget.regionStoreScopes = mergeRegionScopes(
      platformTarget.regionStoreScopes,
      regionStoreScopes,
    );
    platformTarget.storeCount += storeCount;

    return res;
  }, []);
};

export type NewPermissionMap = Partial<Record<General.PermissionName, General.StoreScope[]>>;

export const newPermissionMapState = atom((get) => {
  const { roles } = get(newPermissionState);
  return roles.reduce<NewPermissionMap>((res, role) => {
    return role.permissionWithScopes.reduce((res, { permissionName, platformStoreScopes }) => {
      res[permissionName] = mergePlatformScopes(res[permissionName] || [], platformStoreScopes);

      if (permissionName === '配置催单催付任务') {
        res[permissionName] = res[permissionName]?.filter(
          (_) => _.platform !== 'MERCADO' && _.storeCount > 0,
        );
      }

      return res;
    }, res);
  }, {});
});

export const useGetPermissionScopes = () => {
  const permissionMap = useAtomValue(newPermissionMapState);

  return useCallback(
    (permissionName: General.PermissionName) => {
      return permissionMap[permissionName];
    },
    [permissionMap],
  );
};

export interface GetPermissionProps {
  newPermissionEnable?: boolean;
  newPermissionMap: NewPermissionMap;
  permissionName: General.PermissionName;
  platform?: General.PlatformUpperCaseTunnel;
  region?: General.UpperCaseRegion;
  storeId?: number;
  storeIds?: number[];
  fallbackPermission?: boolean;
}

export const getPermission = (props: GetPermissionProps) => {
  const {
    newPermissionEnable,
    newPermissionMap,
    permissionName,
    platform,
    region,
    storeId,
    storeIds = [],
    fallbackPermission = true,
  } = props;

  if (newPermissionEnable === undefined) {
    return false;
  }

  if (newPermissionEnable === false) {
    return fallbackPermission;
  }

  const { [permissionName]: scopes } = newPermissionMap;

  if (permissionName === '店铺管理') {
    return Boolean(scopes);
  }

  if (!scopes?.length) {
    return false;
  }

  if (platform === undefined) {
    return true;
  }

  const platformTarget = scopes.find((scope) => scope.platform === platform);

  if (platformTarget === undefined || !platformTarget.storeCount) {
    return false;
  }

  if (region === undefined) {
    return true;
  }

  const allRegionTarget = platformTarget.regionStoreScopes.find((scope) => scope.region === 'all');

  if (allRegionTarget) {
    return true;
  }

  const regionTarget = platformTarget.regionStoreScopes.find((scope) => scope.region === region);

  if (regionTarget === undefined || !regionTarget.storeCount) {
    return false;
  }

  const { storeIds: regionStoreIds = [] } = regionTarget;

  if (regionStoreIds.length === 0) {
    return true;
  }

  if (storeId !== undefined) {
    return regionStoreIds.includes(storeId);
  }

  if (storeIds.length === 0) {
    return true;
  }

  return storeIds.some((storeId) => regionStoreIds.includes(storeId));
};

export const useGetPermission = () => {
  const { newPermissionEnable } = useAtomValue(newPermissionState);

  const newPermissionMap = useAtomValue(newPermissionMapState);

  return useCallback(
    (
      permissionName: General.PermissionName,
      props?: Pick<
        GetPermissionProps,
        'platform' | 'region' | 'storeId' | 'storeIds' | 'fallbackPermission'
      >,
    ) => {
      return getPermission({
        newPermissionEnable,
        newPermissionMap,
        permissionName,
        ...props,
      });
    },
    [newPermissionEnable, newPermissionMap],
  );
};

export const useRouteAccessProps = () => {
  const permissionMap = usePermission();

  const orgConfig = useOrgConfigStateValue();

  const orgValue = useOrgValue();

  const { newPermissionEnable } = useAtomValue(newPermissionState);

  const newPermissionMap = useAtomValue(newPermissionMapState);

  const incentiveType = useIncentiveType();

  return useMemo(() => {
    return {
      permissionMap,
      orgConfig,
      orgValue,
      newPermissionEnable,
      newPermissionMap,
      incentiveType,
    };
  }, [permissionMap, orgConfig, orgValue, newPermissionEnable, newPermissionMap, incentiveType]);
};

export const useIsAssignConversation = () => {
  const { roles } = useAtomValue(newPermissionState);

  return useMemo(
    () => roles.some(({ assignConversationConfig }) => assignConversationConfig.assignConversation),
    [roles],
  );
};

export const useNewPermissionMap = () => useAtomValue(newPermissionMapState);
